import {
	useState,
	useContext,
	useMemo,
	useRef,
	useEffect,
	RefObject,
	useCallback,
	memo,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box } from 'src/components/ui';
import CampaignSection from 'src/components/campaigns/CampaignSection';
import SummaryExtraInformation from '../../components/campaigns/SummaryExtraInfo';
import { GatherInfoForm } from 'src/components/common/LazyComponents';
import useAccountConfigOptions from 'src/hooks/config/useAccountConfigOptions';
import {
	generateDesignDirectionsFromLayers,
	generateDesignDirectionsWithTemplates,
	getInterpretations,
} from 'src/services/campaign';
import { regenerateCampaignDesignDirection } from 'src/services/campaign';
import {
	AppInputConfigContext,
	CampaignContext,
	withAppConfigProvider,
} from 'src/contexts';
import useCampaignConfig from 'src/hooks/useCampaignConfig';
import useBudgetManager from 'src/hooks/useBudgetManager';
import { IOption } from 'src/lib/schemas/misc';
import { toastError } from 'src/services/toast';
import {
	createOrUpdateCampaign,
	genrateCampaignCreatives,
} from 'src/services/campaign';
import UserContext from 'src/contexts/user/UserContext';
import { getAvailableChannels } from 'src/services/account';
import {
	ICampaign,
	ICampaignSchedule,
	IChannelCreativeAttributes,
	IChannelGroup,
} from 'src/lib/schemas';
import TemplateContext from 'src/contexts/templates/TemplatesContext';
import DesignDirections from 'src/components/campaigns/design-directions/DesignDirections';
import CampaignSchedule from 'src/components/campaigns/schedule/CampaignSchedule';
import { generateBudgetBreakdownByPlacements } from 'src/components/campaign/CampaignBudget/parts/utils';
import CampaignStepper from 'src/components/campaigns/CampaignStepper';
import { withCampaignProvider } from 'src/contexts';
import { validateCampaignConfig } from 'src/components/campaigns/utils/validations';
import FusionLoading from 'src/components/common/FusionLoading';
import { CampaignHeader } from 'src/components/campaign/parts';
import { cloneDeepWith, isEqual, omit } from 'lodash';
import {
	ICreative,
	IDesignDirection,
} from 'src/lib/schemas/campaign/newFlowCampaign';
import { ICampaignNavigationEvent, IEvent } from 'src/lib/schemas/event/event';
import { processCallback } from 'src/lib/utils/processCallback';
import { unstable_batchedUpdates } from 'react-dom';

interface CustomWindow extends Window {
	fusion?: any;
}
declare const window: CustomWindow;

const CreateEditCampaign = memo(() => {
	// Add render counter for debugging
	const renderCount = useRef(0);
	renderCount.current += 1;

	// Add safety wrapper to prevent crashes
	try {
		const [searchParams] = useSearchParams();
		const fromCopilot = searchParams.get('from') === 'copilot';

		const {
			campaign,
			id: campaignId,
			setCampaign,
			enabledSections,
			openSections,
			setOpenSections,
			oldCampaignCopy,
			setOldCampaignCopy,
			setWasCampaignChanged,
			isFetching: isCampaignFetching,
		} = useContext(CampaignContext);

		const { updateBudget } = useBudgetManager();

		// ALL HOOKS MUST BE CALLED HERE, BEFORE ANY CONDITIONAL LOGIC
		const [group, setGroup] = useState<string | undefined>();
		const [campaignTitle, setCampaignTitle] = useState('');
		const [metaOptions, setMetaOptions] = useState<IOption[]>([]);
		const [availableChannelGroups, setAvailableChannelGroups] = useState<
			IChannelGroup[] | null
		>(null);
		const [availableCreatives, setAvailableCreatives] =
			useState<IChannelCreativeAttributes[]>();
		const [isLoadingChannels, setIsLoadingChannels] = useState(false);
		const [isGeneratingBrief, setIsGeneratingBrief] = useState(false);
		const [designDirections, setDesignDirections] = useState(
			campaign?.designDirections ?? ([] as Array<IDesignDirection>),
		);
		const [creatives, setCreatives] = useState(
			campaign?.creatives ?? ([] as Array<ICreative>),
		);
		const [isGeneratingDesignDirections, setIsGeneratingDesignDirections] =
			useState(false);

		// REMOVED: local isLoading state - using isCampaignFetching from context instead
		const designDirectionsRef = useRef<HTMLDivElement>(null);
		const gatherInfoRef = useRef<HTMLDivElement>(null);
		const scheduleRef = useRef<HTMLDivElement>(null);
		const { inputConfig } = useContext(AppInputConfigContext);

		const { account } = useContext(UserContext);
		const {
			selectedTemplateIds,
			setSelectedTemplateIds,
			setSelectTemplateModalOpen,
		} = useContext(TemplateContext);
		const [scrappedDataLoading, setScrappedDataLoading] = useState(false);
		const tonesOptions = inputConfig?.['Tone'] ?? [];
		const audienceOptions = inputConfig?.['Audience'] ?? [];
		const currentOpenSectionIndex = openSections.findIndex((isOpen) => isOpen);
		const { fetchConfig: fetchTones } = useAccountConfigOptions('Tone');
		const { fetchConfig: fetchAudiences } = useAccountConfigOptions('Audience');
		const [hasLoadedInitialPlacements, setHasLoadedInitialPlacements] =
			useState(false);
		const [selectedPlacements, setSelectedPlacements] = useState<string[]>([]);
		const [selectedChannels, setSelectedChannels] = useState<string[]>([]);
		const [forcedOpenSections, setForcedOpenSections] = useState<
			boolean[] | undefined
		>(undefined);
		const [forcedActiveSection, setForcedActiveSection] = useState<
			number | undefined
		>(undefined);
		const [preventScroll, setPreventScroll] = useState(false);
		const [userClickedStep3, setUserClickedStep3] = useState(false);
		const [isGeneratingBudget, setIsGeneratingBudget] = useState(false);
		const [isBudgetLoading, setIsBudgetLoading] = useState(false);
		const [hasInitialCopilotNavigation, setHasInitialCopilotNavigation] =
			useState(false);

		// ALL useEffect hooks must be called here, before any conditional logic
		const prevCampaignRef = useRef(campaign);
		const selectedTemplateIdsRef = useRef<string[]>([]);
		const previousPlacementsRef = useRef(campaign?.placements);
		const hasFirstUpdateOccurred = useRef(false);
		const autoBreakdownInitializedRef = useRef<Set<string>>(new Set()); // Track which campaigns have had auto breakdown initialized
		const isInitializingCampaignRef = useRef(true); // Track if we're still in initialization phase

		// Memoize context values to prevent unnecessary re-renders
		const contextValues = useMemo(
			() => ({
				campaignId,
				campaign: campaign
					? {
							id: campaign.id,
							status: campaign.status,
							designDirectionsCount: campaign.designDirections?.length,
							budget: campaign.budget,
						}
					: null,
				enabledSections,
				openSections,
				isCampaignFetching,
			}),
			[
				campaignId,
				campaign?.id,
				campaign?.status,
				campaign?.designDirections?.length,
				campaign?.budget,
				enabledSections,
				openSections,
				isCampaignFetching,
			],
		);

		// Track campaign changes
		useEffect(() => {
			if (prevCampaignRef.current !== campaign) {
				prevCampaignRef.current = campaign;
				// Sync local state with campaign context
				if (campaign?.designDirections) {
					setDesignDirections(campaign.designDirections);
				}
				if (campaign?.creatives) {
					setCreatives(campaign.creatives);
				}
			}
		}, [campaign]);

		// Initialize tones and audiences
		useEffect(() => {
			fetchTones();
			fetchAudiences();
			return () => {
				setSelectedTemplateIds([]);
			};
		}, []); // Removed fetchTones and fetchAudiences from dependencies

		// Update designDirections and creatives when campaign changes
		useEffect(() => {
			if (campaign) {
				unstable_batchedUpdates(() => {
					setDesignDirections(campaign.designDirections ?? []);
					setCreatives(campaign.creatives ?? []);
				});
			}
		}, [campaign?.id, campaign?.designDirections, campaign?.creatives]);

		// Process fusion events
		useEffect(() => {
			const processEvent = (event: IEvent<any>) => {
				if (event.target == 'campaign') {
					if (event.action == 'setNavigation') {
						const navigationEvent = event as IEvent<ICampaignNavigationEvent>;
						const sections = navigationEvent.value.sections;
						const activeSection = navigationEvent.value.activeSection;
						const preventScrollFlag = navigationEvent.value.preventScroll;
						unstable_batchedUpdates(() => {
							setForcedOpenSections(sections);
							setForcedActiveSection(activeSection);
							setPreventScroll(preventScrollFlag || false);
						});
					}
				}
			};

			if (window.fusion && Array.isArray(window.fusion)) {
				while (window.fusion.length) {
					processEvent(window.fusion.pop());
				}
			}
			window.fusion = { push: processEvent };
		}, []);

		// Handle sections and navigation
		useEffect(() => {
			if (
				!campaign?.placements &&
				campaignId === 'new' &&
				availableChannelGroups?.length
			) {
				const defaultPlacements = availableChannelGroups.flatMap((group) =>
					group.channels.flatMap(
						(channel) =>
							channel.placements?.map((placement) => placement.id) || [],
					),
				);
				setSelectedPlacements(defaultPlacements);
			}

			const highestEnabledIndex = enabledSections
				.map((enabled, index) => (enabled ? index : -1))
				.reduce((max, current) => (current > max ? current : max), -1);

			// Determine which section should be open based on campaign state
			let sectionToOpen = highestEnabledIndex;

			// Always open Step 2 when there are design directions unless user explicitly clicked Step 3
			if (campaign?.designDirections?.length && !userClickedStep3) {
				sectionToOpen = 1; // Step 2
			}

			if (forcedOpenSections) {
				setOpenSections(forcedOpenSections);

				// If Step 3 is being opened via forcedOpenSections, set userClickedStep3 to true
				if (forcedOpenSections[2]) {
					setUserClickedStep3(true);
				}

				// Reset forcedOpenSections after applying it so normal logic can take over
				setTimeout(() => {
					setForcedOpenSections(undefined);
				}, 100);
			} else {
				setOpenSections((prev) =>
					prev.map((_, index) => index === sectionToOpen),
				);
			}

			const selectedIndex = forcedActiveSection ?? sectionToOpen;

			// Check if we're already on the section we want to open (no scroll needed)
			const currentlyOpenSection = openSections.findIndex(
				(section) => section === true,
			);
			const shouldScroll =
				!preventScroll && selectedIndex !== currentlyOpenSection;

			if (shouldScroll) {
				switch (selectedIndex) {
					case 1:
						scrollIntoRef(designDirectionsRef);
						break;
					case 2:
						scrollIntoRef(scheduleRef);
						break;
					default:
						break;
				}
			}
		}, [
			availableChannelGroups,
			campaignId,
			forcedOpenSections,
			forcedActiveSection,
			preventScroll,
			fromCopilot,
			enabledSections,
			userClickedStep3,
		]);

		// Handle copilot navigation
		useEffect(() => {
			// Handle copilot navigation without scroll - this runs when campaign changes
			if (
				fromCopilot &&
				campaign &&
				!forcedOpenSections &&
				!hasInitialCopilotNavigation
			) {
				unstable_batchedUpdates(() => {
					// Always set step 1 closed and step 2 open when coming from copilot
					setOpenSections([false, true, false, false]);
					setHasInitialCopilotNavigation(true);
				});

				// Scroll to top of page with proper timing
				setTimeout(() => {
					window.scrollTo({ top: 0, behavior: 'smooth' });
				}, 100);
				return;
			}

			// The openSections are now handled by the CampaignProvider
			// Only override if forcedOpenSections is provided
			if (forcedOpenSections !== undefined) {
				setOpenSections(forcedOpenSections);
			}
		}, [
			campaign?.id,
			fromCopilot,
			forcedOpenSections,
			hasInitialCopilotNavigation,
		]);

		// Load initial placements and group
		useEffect(() => {
			if (campaign && !hasLoadedInitialPlacements) {
				unstable_batchedUpdates(() => {
					if (campaign.placements) {
						setSelectedPlacements(campaign.placements);
					}
					if (campaign.group) {
						setGroup(campaign.group);
					}
					setHasLoadedInitialPlacements(true);
				});
			}
		}, [campaign?.id, hasLoadedInitialPlacements]);

		// Load channels
		useEffect(() => {
			const getChannels = async () => {
				if (!account?.id) return;
				setIsLoadingChannels(true);
				const { channels, creatives } = await getAvailableChannels(account.id);
				unstable_batchedUpdates(() => {
					setAvailableChannelGroups(channels);
					setAvailableCreatives(creatives);
					setIsLoadingChannels(false);
				});
			};
			getChannels();
		}, [account?.id]);

		// Calculate budget breakdown when availableChannelGroups loads for campaigns with budget but no breakdown
		useEffect(() => {
			const campaignKey = campaign?.id || 'new';

			if (
				availableChannelGroups &&
				campaign?.budget &&
				campaign.budget.total > 0 &&
				(!campaign.budget.breakdown ||
					campaign.budget.breakdown.length === 0) &&
				!isGeneratingBudget &&
				!autoBreakdownInitializedRef.current.has(campaignKey) // Only initialize once per campaign
			) {
				// Mark this campaign as having had auto breakdown initialized
				autoBreakdownInitializedRef.current.add(campaignKey);
				setIsGeneratingBudget(true);

				updateBudget({
					total: campaign.budget.total,
					placements: campaign.placements,
					availableChannelGroups: availableChannelGroups,
					source: 'Auto breakdown on channels load',
				})
					.catch((error) => {
						toastError(error);
					})
					.finally(() => {
						setIsGeneratingBudget(false);
					});
			}
		}, [
			availableChannelGroups,
			campaign?.id, // Only depend on campaign ID, not the entire budget object
			isGeneratingBudget,
		]);

		// Update selectedTemplateIds ref
		useEffect(() => {
			selectedTemplateIdsRef.current = selectedTemplateIds;
		}, [selectedTemplateIds]);

		// Reset initialization flag when campaign changes
		useEffect(() => {
			isInitializingCampaignRef.current = true;
		}, [campaignId]);

		// Mark initialization as complete when campaign and channel groups are loaded
		useEffect(() => {
			if (
				campaign &&
				availableChannelGroups &&
				availableChannelGroups.length > 0
			) {
				// Wait a bit to ensure all initial updates have completed
				const timer = setTimeout(() => {
					isInitializingCampaignRef.current = false;
				}, 1000); // 1 second delay

				return () => clearTimeout(timer);
			}
		}, [campaign, availableChannelGroups]);

		// Handle placements changes
		useEffect(() => {
			const currentPlacementsString = JSON.stringify(campaign?.placements);
			const previousPlacementsString = JSON.stringify(
				previousPlacementsRef.current,
			);

			if (currentPlacementsString !== previousPlacementsString) {
				if (!hasFirstUpdateOccurred.current) {
					hasFirstUpdateOccurred.current = true;
				} else {
					// Only update budget if:
					// 1. Campaign is not new
					// 2. Budget already exists with breakdown (not during initial setup)
					// 3. We're not currently generating budget
					// 4. Placements actually changed in a meaningful way (length or content)
					// 5. Auto breakdown has already been initialized for this campaign
					const campaignKey = campaign?.id || 'new';
					const placementsLengthChanged =
						(campaign?.placements?.length || 0) !==
						(previousPlacementsRef.current?.length || 0);
					const placementsContentChanged =
						currentPlacementsString !== previousPlacementsString;
					const hasBreakdown = (campaign?.budget?.breakdown?.length || 0) > 0;

					if (
						campaign?.id !== 'new' &&
						campaign?.budget &&
						hasBreakdown && // Only if budget breakdown already exists
						!isGeneratingBudget &&
						!isInitializingCampaignRef.current && // Only after initialization is complete
						(placementsLengthChanged || placementsContentChanged) &&
						previousPlacementsRef.current // Only if we had previous placements (not initial load)
					) {
						updateCampaignBudget();
					}
				}

				previousPlacementsRef.current = campaign?.placements;
			}
		}, [campaign?.placements, campaign?.id, isGeneratingBudget]);

		// Generate budget when Step 3 is opened
		useEffect(() => {
			if (
				openSections[2] &&
				userClickedStep3 &&
				campaign &&
				campaign.budget &&
				(!campaign.budget.breakdown ||
					campaign.budget.breakdown.length === 0) &&
				availableChannelGroups &&
				!isGeneratingBudget
			) {
				unstable_batchedUpdates(() => {
					setIsGeneratingBudget(true);
					setIsBudgetLoading(true);
				});
				try {
					// Use centralized budget manager to calculate breakdown
					updateBudget({
						total: campaign.budget.total,
						placements: campaign.placements,
						availableChannelGroups: availableChannelGroups || undefined,
						source: 'Step3 breakdown initialization',
					}).catch((error) => {
						toastError(error);
					});
				} catch (error: any) {
					toastError(error);
				} finally {
					unstable_batchedUpdates(() => {
						setIsGeneratingBudget(false);
						setIsBudgetLoading(false);
					});
				}
			}
		}, [
			openSections[2],
			userClickedStep3,
			campaign?.id,
			availableChannelGroups,
			campaignId,
			isGeneratingBudget,
		]);

		// Fetch old campaign copy
		useEffect(() => {
			if (!campaignId || campaignId === 'new') return;
			const fetchCampaign = async () => {
				try {
					// Use campaign from context instead of making a separate API call
					if (campaign) {
						setOldCampaignCopy(cloneDeepWith(campaign));
					}
				} catch (error) {}
			};

			fetchCampaign();
		}, [campaignId, campaign?.id, setOldCampaignCopy]);

		// Load initial data - simplified to only fetch tones and audiences
		useEffect(() => {
			const loadData = async () => {
				try {
					await fetchTones();
					await fetchAudiences();
					if (account?.id) {
						setIsLoadingChannels(true);
						const { channels, creatives } = await getAvailableChannels(
							account.id,
						);
						unstable_batchedUpdates(() => {
							setAvailableChannelGroups(channels);
							setAvailableCreatives(creatives);
							setIsLoadingChannels(false);
						});
					}
				} catch (error) {
					toastError(error);
				}
			};

			loadData();
		}, [account?.id, campaignId]);

		// NOW ALL HOOKS ARE CALLED, WE CAN CONTINUE WITH THE REST OF THE LOGIC
		const compareCampaigns = useCallback(
			(newCampaign: any, oldCampaign: any, fieldsToOmit: string[]) => {
				const newCampaignCleaned = omit(newCampaign, fieldsToOmit);
				const oldCampaignCleaned = omit(oldCampaign, fieldsToOmit);
				return isEqual(newCampaignCleaned, oldCampaignCleaned);
			},
			[],
		);

		const updateCampaignBudget = useCallback(
			async (schedule?: ICampaignSchedule) => {
				if (!campaignId || !campaign || !campaign.budget) return;

				try {
					// Use centralized budget manager for budget updates
					await updateBudget({
						total: campaign.budget.total,
						placements: campaign.placements,
						availableChannelGroups: availableChannelGroups || undefined,
						source: 'updateCampaignBudget - placements change',
					});

					// If we also need to update schedule, do a separate API call
					if (schedule) {
						const effectiveCampaignId =
							campaignId === 'new' && campaign?.id ? campaign.id : campaignId;

						await createOrUpdateCampaign({ schedule }, effectiveCampaignId);
					}
				} catch (error: any) {
					toastError(error);
				}
			},
			[
				campaignId,
				campaign?.placements,
				campaign?.budget,
				availableChannelGroups,
				updateBudget,
			],
		);

		const availablePlacements = useMemo(
			() =>
				(availableChannelGroups || []).flatMap(
					(channelGroup) => channelGroup.channels,
				),
			[availableChannelGroups],
		);

		const getCampaignBrief = useCallback(async () => {
			try {
				const { callback } = await getInterpretations(campaign?.id || '');
				const { brief_description } = await processCallback(callback);
				const brief = { title: campaignTitle, description: brief_description };
				return brief;
			} catch (error) {
				console.error(error);
			}
		}, [campaign, campaignTitle]);

		const handleCampaignSubmit = useCallback(
			async (layeredTemplates?: any[]) => {
				if (!campaignId || !campaign) return;

				// Navigate to step 2 when user actively generates design directions
				// This is expected behavior - user wants to see the generation progress
				unstable_batchedUpdates(() => {
					setForcedOpenSections([false, true, false, false]);
					setOpenSections([false, true, false, false]);
				});

				// Delay scroll to allow modal closing animation to complete
				setTimeout(() => {
					scrollIntoRef(designDirectionsRef);
				}, 300);

				unstable_batchedUpdates(() => {
					setIsGeneratingBrief(true);
					setIsGeneratingDesignDirections(true);
				});
				try {
					const brief = await getCampaignBrief();
					// Use actual campaign ID if available instead of 'new'
					const effectiveCampaignId =
						campaignId === 'new' && campaign?.id ? campaign.id : campaignId;

					const updatedCampaign = await createOrUpdateCampaign(
						{ brief },
						effectiveCampaignId,
					);
					unstable_batchedUpdates(() => {
						setCampaign(updatedCampaign);
						setOldCampaignCopy(updatedCampaign);
						setSelectedTemplateIds([]);
					});
					await handleGenerateDesignDirections(layeredTemplates);
				} catch (error: any) {
					toastError(error);
				} finally {
					unstable_batchedUpdates(() => {
						setIsGeneratingBrief(false);
					});
				}
			},
			[
				campaignId,
				campaign,
				getCampaignBrief,
				setCampaign,
				setOldCampaignCopy,
				setSelectedTemplateIds,
			],
		);

		const handleRegenerateDesignDirections = useCallback(async () => {
			if (!campaignId) return;
			scrollIntoRef(designDirectionsRef);

			for (const designDirection of designDirections ?? []) {
				if (!designDirection.locked) {
					try {
						await regenerateCampaignDesignDirection(
							campaignId,
							designDirection.id,
							{},
						);
					} catch (error) {
						console.error(
							`Error regenerating design direction ${designDirection.id}:`,
							error,
						);
					}
				}
			}
			if (campaign) {
				campaign.designDirections &&
					setDesignDirections(campaign.designDirections);
				campaign.creatives && setCreatives(campaign.creatives);
			}
		}, [campaignId, designDirections, campaign]);

		const handleScheduleAndPublish = useCallback(async () => {
			if (!campaignId) return;

			handleOpenSection(2);

			if (openSections[2]) {
				scrollIntoRef(scheduleRef);
			}

			try {
				if (!campaign) return;
				await genrateCampaignCreatives(campaign, '', true);
			} catch (error) {
				console.error('Error generating creatives:' + error);
			}
		}, [campaignId]);

		const handleGenerateDesignDirections = useCallback(
			async (layeredTemplates?: any[]) => {
				if (!campaignId) return;
				try {
					// Only navigate to Step 2 if this is a NEW generation (from Step 1)
					// For regenerations or template changes, keep current step state
					const isNewGeneration =
						layeredTemplates && layeredTemplates.length > 0;

					if (isNewGeneration) {
						// User is actively generating new design directions - show them Step 2
						unstable_batchedUpdates(() => {
							setForcedOpenSections([false, true, false, false]);
							setOpenSections([false, true, false, false]);
						});
						scrollIntoRef(designDirectionsRef);
					}

					if (!campaign) return;
					if (layeredTemplates) {
						await generateDesignDirectionsFromLayers(
							campaign,
							layeredTemplates,
						);
					} else {
						await generateDesignDirectionsWithTemplates(
							campaign,
							selectedTemplateIdsRef.current,
						);
					}
				} catch (error: any) {
					toastError(error);
				}
			},
			[campaignId, campaign],
		);

		const scrollIntoRef = useCallback(
			(ref: RefObject<HTMLDivElement>, offset: number = 400) => {
				if (!ref || !ref.current) return;
				const elementPosition =
					ref.current.getBoundingClientRect().top + window.scrollY;
				const offsetPosition = elementPosition - offset;
				setTimeout(() => {
					window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
				}, 50);
			},
			[],
		);

		const toggleSection = useCallback(
			(index: number) => {
				setOpenSections((prev) => {
					const newSections = prev.map((_, idx) => idx === index);
					return newSections;
				});
			},
			[setOpenSections],
		);

		const handleOpenTemplateModal = useCallback(async () => {
			setSelectTemplateModalOpen(true);
		}, [setSelectTemplateModalOpen]);

		const handleSubmitSchedule = useCallback(
			(schedule: ICampaignSchedule) => {
				updateCampaignBudget(schedule);
			},
			[updateCampaignBudget],
		);

		const handleOpenSection = useCallback(
			async (section: number) => {
				// Track when user clicks on steps
				if (section === 2) {
					setUserClickedStep3(true);
				} else if (section === 1) {
					setUserClickedStep3(false);
				}

				const fieldsToOmit = [
					'createdAt',
					'creatives',
					'designDirections',
					'lastUpdatedBy',
					'updatedAt',
					'placements',
					'channels',
					'budget',
					'variants',
				];

				const isStepOneOpen =
					openSections.length === 4 &&
					openSections.every(
						(value, index) => value === [true, false, false, false][index],
					);

				const isCampaignUnchanged = compareCampaigns(
					campaign,
					oldCampaignCopy,
					fieldsToOmit,
				);

				if (isCampaignUnchanged) {
					toggleSection(section);
				} else {
					setWasCampaignChanged(true);
					if (!isStepOneOpen) {
						toggleSection(section);
						return;
					}
					const configFormSubmitElement = document.getElementById(
						'campaign-config-form-submit-with-lock-modal',
					);
					configFormSubmitElement?.click();
				}
			},
			[
				openSections,
				compareCampaigns,
				campaign,
				oldCampaignCopy,
				toggleSection,
				setWasCampaignChanged,
			],
		);

		const handleMetaOptions = useCallback((options: IOption[]) => {
			if (options.length) {
				setMetaOptions(options);
			}
		}, []);

		const isValidCampaignConfig = useMemo(() => {
			if (!campaign) return false;
			return validateCampaignConfig(campaign, metaOptions ?? []);
		}, [
			campaign?.id,
			campaign?.status,
			campaign?.placements,
			campaign?.brief,
			metaOptions,
		]);

		// Show loading state while fetching campaign data
		if (isCampaignFetching) {
			return (
				<Box p={4}>
					<FusionLoading />
				</Box>
			);
		}

		// If campaignId is undefined, user navigated away from campaigns - don't render
		if (!campaignId) {
			return null;
		}

		// Show error state if no campaign and not a new campaign, but only if not currently fetching
		if (
			!campaign &&
			campaignId &&
			campaignId !== 'new' &&
			!isCampaignFetching
		) {
			return (
				<Box p={4}>
					<div>Campaign not found or failed to load.</div>
				</Box>
			);
		}

		return (
			<Box p={{ base: 2, md: 4 }} pt={{ base: '72px', md: '0px' }} w="full" maxW="1400px" mx="auto">
				<CampaignHeader
					campaignDetail={{
						title: !campaignTitle ? 'New Campaign' : campaignTitle,
						group,
						lastEditTime: campaign?.updatedAt,
						lastEditBy: campaign?.lastUpdatedBy?.name,
					}}
					onGroupChange={(value) => setGroup(value)}
					scrappedDataLoading={scrappedDataLoading}
				/>
				<CampaignStepper
					handleOpenSection={handleOpenSection}
					designDirectionsRef={designDirectionsRef}
					scheduleRef={scheduleRef}
					scrollIntoRef={scrollIntoRef}
					currentOpenSectionIndex={currentOpenSectionIndex}
				/>

				{/* REMOVED: FusionLoading with local isLoading state */}
				<Box display="block" ref={gatherInfoRef} pt={{ base: '80px', md: '100px' }}>
					<CampaignSection
						title="Step 1: Gather Information"
						isOpen={openSections[0]}
						onToggle={() => toggleSection(0)}
						stepIdx={0}
						accordionContent={
							<GatherInfoForm
								group={group}
								availableChannels={availablePlacements}
								availableChannelGroups={availableChannelGroups}
								onCampaignTitleChange={setCampaignTitle}
								scrappedDataLoading={scrappedDataLoading}
								onCampaignSubmit={handleCampaignSubmit}
								onMetaOptionsChange={handleMetaOptions}
								setOpenSections={setOpenSections}
								selectedPlacements={selectedPlacements}
								onTemplateGalleryOpen={handleOpenTemplateModal}
								setSelectedPlacements={setSelectedPlacements}
								designDirections={designDirections}
								setDesignDirections={setDesignDirections}
								setCreatives={setCreatives}
								onRegenerateDesignDirections={handleRegenerateDesignDirections}
								onChannelsChange={setSelectedChannels}
								setScrappedDataLoading={setScrappedDataLoading}
								setIsGeneratingDesignDirections={
									setIsGeneratingDesignDirections
								}
							/>
						}
						extraInformation={
							isGeneratingBrief ? (
								<SummaryExtraInformation />
							) : campaign?.brief ? (
								<SummaryExtraInformation />
							) : undefined
						}
						isDisabled={!enabledSections[0]}
					/>
					<Box ref={designDirectionsRef} pt={{ base: '0px', md: '20px' }} />
					<CampaignSection
						title="Step 2: Generate Designs"
						isOpen={openSections[1]}
						stepIdx={1}
						infoMessage="Each design version competes in real-time. We track performance and automatically focus on the winner."
						accordionContent={
							<DesignDirections
								creativesConfig={availableCreatives ?? []}
								isLoading={isGeneratingDesignDirections}
								onSubmit={handleScheduleAndPublish}
								designDirections={designDirections}
								setDesignDirections={setDesignDirections}
								creatives={creatives}
								setCreatives={setCreatives}
								availableChannelGroups={availableChannelGroups ?? []}
								selectedChannels={selectedChannels}
								isGeneratingFromModalShared={isGeneratingDesignDirections}
								setIsGeneratingDesignDirections={
									setIsGeneratingDesignDirections
								}
							/>
						}
						onToggle={() => handleOpenSection(1)}
						isDisabled={!enabledSections[1]}
					/>
					<Box ref={scheduleRef} pt={{ base: '0px', md: '20px' }} />
					<CampaignSection
						title="Step 3: Finalize"
						isOpen={openSections[2]}
						stepIdx={2}
						accordionContent={
							<CampaignSchedule
								schedule={campaign?.schedule}
								selectedChannels={selectedChannels}
								onScheduleSubmit={handleSubmitSchedule}
								availableChannelGroups={availableChannelGroups}
								requirementsLabelStyle={{ color: '#718096', fontSize: '14px' }}
								isValidCampaignConfig={isValidCampaignConfig}
								designDirections={designDirections}
								creatives={creatives}
								isBudgetLoading={isBudgetLoading}
							/>
						}
						onToggle={() => handleOpenSection(2)}
						isDisabled={!enabledSections[2] || designDirections.length === 0}
					/>
				</Box>
			</Box>
		);
	} catch (error: any) {
		console.error('Error rendering CreateEditCampaign:', error);

		// Capture error to PostHog for mobile debugging
		import('posthog-js')
			.then((m) => m.default)
			.then((posthog) => {
				posthog.captureException(error, {
					$exception_message: error?.message || 'Unknown error',
					$exception_type: error?.name || 'Error',
					$exception_stack_trace_raw: error?.stack,
					component: 'CreateEditCampaign',
					url: window.location.href,
					campaignId: window.location.pathname.match(/\/projects\/campaigns\/([^?\/]+)/)?.[1],
				});

				posthog.capture('campaign_page_render_error', {
					error_message: error?.message || 'Unknown error',
					error_name: error?.name || 'Error',
					error_stack: error?.stack,
					page_url: window.location.href,
					campaign_id: window.location.pathname.match(/\/projects\/campaigns\/([^?\/]+)/)?.[1],
				});
			})
			.catch((posthogError) => {
				console.error('Failed to capture error to PostHog:', posthogError);
			});

		return (
			<Box p={4}>
				<div>An unexpected error occurred.</div>
			</Box>
		);
	}
});

CreateEditCampaign.displayName = 'CreateEditCampaign';

const CreateEditCampaignWithProvider = withCampaignProvider(CreateEditCampaign);
const CreateEditCampaignWithAppConfigProvider = withAppConfigProvider(
	CreateEditCampaignWithProvider,
);

export default CreateEditCampaignWithAppConfigProvider;
